import Ty from '@mui/material/Typography';
import { isNil } from 'lodash';
import React, { Dispatch, SetStateAction } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import ActionsDialog from '../ActionsDialog/ActionsDialog';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { useUpdateIsActiveGlAccountsMutation } from '../../../api/sales/mutations';
import { CreateGlAccountRequest, GlAccount } from '../../../api/sales/requests';
import SalesUtilities from '../../../sales/utilities';
import GeneralLedgerAccountsDialogContent from './GeneralLedgerAccountsDialogContent';
import getEditDialogContent from './helpers';
import { dialogSnackbarMessage, dialogTitleContent } from './styles';

export interface GeneralLedgerAccountsEditDialogProps {
  open: boolean;
  deactivate: boolean | undefined;
  hideInInvoicing: boolean | undefined;
  onClose: () => void;
  glAccounts: GlAccount[];
  handleUpdate: (requestData: CreateGlAccountRequest) => Promise<void>;
  data: CreateGlAccountRequest | undefined;
  onSuccess: () => void;
  bulkAction?: boolean;
  setBulkActionAccountTree: Dispatch<SetStateAction<GlAccount[] | undefined>>;
  fromActionsMenu: boolean;
  setFromActionsMenu: Dispatch<SetStateAction<boolean>>;
}

function GeneralLedgerAccountsEditDialog(props: GeneralLedgerAccountsEditDialogProps) {
  const {
    open,
    deactivate,
    hideInInvoicing,
    onClose,
    data,
    handleUpdate,
    glAccounts,
    bulkAction,
    setBulkActionAccountTree,
    onSuccess,
    fromActionsMenu,
    setFromActionsMenu,
  } = props;
  const { t } = useTranslation();
  const { setSnackbarState } = useSnackbar();
  const history = useHistory();
  const { mutateAsync: updateIsActiveGlAccounts } = useUpdateIsActiveGlAccountsMutation();

  const withChildren = glAccounts[0].subAccounts && glAccounts[0].subAccounts.length > 0;

  const { actionTitle, actionText, actionStart, actionButtonText, actionDetail, didToggleBothFlags } =
    getEditDialogContent(t, deactivate, hideInInvoicing);

  const additionalDialogTitleContent = () => {
    let content: React.ReactNode = null;
    if (!actionText) return content;
    if (glAccounts.length === 1) {
      content = (
        <Ty sx={dialogTitleContent} data-testid='gl-accounts-edit-modal-title-content'>
          {!withChildren ? (
            t('admin.glAccountConfiguration.actionContent.additionalDialogTitleSingleAccount', {
              action: actionText,
            })
          ) : (
            <Trans
              i18nKey={
                didToggleBothFlags
                  ? 'admin.glAccountConfiguration.actionContent.multiActionDialogTitleSingleAndSub'
                  : 'admin.glAccountConfiguration.actionContent.additionalDialogTitleSingleAndSub'
              }
              values={{
                numberOfGlAccounts: SalesUtilities.getNumberOfAccountsInTree(glAccounts[0]),
                action: actionText,
                actionStart,
                actionDetail,
              }}
            />
          )}
        </Ty>
      );
    }
    if (glAccounts.length > 1) {
      content = (
        <Ty sx={dialogTitleContent} data-testid='gl-accounts-edit-modal-title-content'>
          <Trans
            i18nKey={
              SalesUtilities.checkIfSubAccountsExist(glAccounts)
                ? 'admin.glAccountConfiguration.actionContent.additionalDialogTitleMultipleAndSub'
                : 'admin.glAccountConfiguration.actionContent.additionalDialogTitleMultipleAccounts'
            }
            values={{
              numberOfGlAccounts: SalesUtilities.getNumberOfGlAccounts(glAccounts),
              action: actionText,
              actionStart,
            }}
          />
        </Ty>
      );
    }
    return content;
  };

  const handleActionClick = async () => {
    if (bulkAction) {
      const accountIds: string[] = [];
      glAccounts.forEach((account) => {
        const subAccountIds = SalesUtilities.getAllGlAccountIds(account as GlAccount);
        accountIds.push(...subAccountIds);
      });
      const result = await updateIsActiveGlAccounts({
        isActive: !deactivate,
        isActiveAffectedAccountIds: accountIds,
      });
      if (result === true) {
        onSuccess();
        setSnackbarState({
          open: true,
          message:
            glAccounts.length === 1 ? (
              <Trans
                i18nKey={
                  !glAccounts[0].subAccounts.length && !fromActionsMenu
                    ? 'admin.glAccountConfiguration.actionContent.successMessageNoChildren'
                    : 'admin.glAccountConfiguration.actionContent.successMessageWithChildren'
                }
                values={{
                  glAccountNumber: glAccounts[0].accountNumber,
                  glAccountName: glAccounts[0].accountName,
                  action: t('admin.glAccountConfiguration.actionContent.updated'),
                }}
                components={{ primary: <Ty sx={dialogSnackbarMessage} display='inline' /> }}
              />
            ) : (
              t('admin.glAccountConfiguration.actionContent.bulkSuccess', {
                action: t('admin.glAccountConfiguration.actionContent.updated'),
              })
            ),
          color: 'success',
          actionText: t('buttonText.view'),
          actionCallback: !fromActionsMenu ? () => history.push('/activity-log') : undefined,
        });
        setBulkActionAccountTree(undefined);
        setFromActionsMenu(false);
      }
    } else if (data) {
      handleUpdate(data);
    }
  };

  const dialogTitle = () => {
    let title = '';
    if (!actionTitle) return title;
    if (glAccounts.length === 1) {
      title = t('admin.glAccountConfiguration.actionContent.actionTitleWithGlAccount', {
        glAccountNumber: glAccounts[0].accountNumber,
        glAccountName: glAccounts[0].accountName,
        action: actionTitle,
        actionDetail,
      });
    } else if (glAccounts.length > 1) {
      title = t('admin.glAccountConfiguration.actionContent.actionTitleBulkActions', {
        action: actionTitle,
      });
    }
    return title;
  };

  return (
    <ActionsDialog
      open={open}
      onClose={onClose}
      dialogTitle={dialogTitle()}
      additionalDialogTitleContent={additionalDialogTitleContent()}
      additionalDialogContent={
        <GeneralLedgerAccountsDialogContent
          glAccounts={glAccounts}
          endMessage={deactivate ? t('admin.glAccountConfiguration.actionContent.existingDataInfo') : undefined}
        />
      }
      closeButtonText={t('buttonText.cancel')}
      closeButtonVariant='text'
      disableActionButton={isNil(deactivate) && isNil(hideInInvoicing)}
      actionButtonText={actionButtonText}
      actionButtonVariant='contained'
      onActionClick={handleActionClick}
      dataTestId='gl-accounts-edit-modal'
    />
  );
}

GeneralLedgerAccountsEditDialog.defaultProps = {
  bulkAction: false,
};

export default GeneralLedgerAccountsEditDialog;
